import { categoryLabels } from "../opportunities/api.js";
import { Field, inputClass } from "../routes/SignIn.js";
import type { ProfileDraft } from "./api.js";
import { capabilityOptionsFor } from "./capabilities.js";
import { AiBadge, Card, cn, PhotoGrid, PrimaryButton, TagEditor, Toggle } from "./ui.js";

/**
 * Step 3 — review the AI-built profile as editable CARDS (not one big form).
 * The whole point: it should feel like reviewing finished work. Each card shows
 * an "AI-filled" badge when the draft came from a crawl, and everything is
 * editable / removable inline.
 */
export function ReviewStep({
  draft,
  onChange,
  onContinue,
}: {
  draft: ProfileDraft;
  onChange: (patch: Partial<ProfileDraft>) => void;
  onContinue: () => void;
}) {
  const fromWebsite = draft.source === "website";
  const badge = fromWebsite ? <AiBadge /> : undefined;

  return (
    <main className="mx-auto max-w-md px-5 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-charcoal">
          {fromWebsite ? "Here's your profile" : "Tell us about your business"}
        </h1>
        <p className="mt-2 text-sm text-charcoal">
          {fromWebsite
            ? "We built this from your website. Look it over and fix anything that's off — nothing's saved yet."
            : "Fill in what you can. You can always edit this later."}
        </p>
      </header>

      <div className="space-y-5">
        {/* 1 — Business Info */}
        <Card icon={<BuildingIcon />} title="Business info" badge={badge}>
          {draft.logoUrl && (
            <img
              src={draft.logoUrl}
              alt="Business logo"
              className="h-14 w-auto max-w-[160px] rounded-lg border border-charcoal/15 bg-white object-contain p-1"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          )}
          <Field label="Business name">
            <input
              className={inputClass}
              value={str(draft.businessName)}
              onChange={(e) => onChange({ businessName: nz(e.target.value) })}
            />
          </Field>
          <Field label="Phone">
            <input
              className={inputClass}
              inputMode="tel"
              value={str(draft.phone)}
              onChange={(e) => onChange({ phone: nz(e.target.value) })}
            />
          </Field>
          <Field label="Email">
            <input
              className={inputClass}
              inputMode="email"
              autoCapitalize="none"
              value={str(draft.email)}
              onChange={(e) => onChange({ email: nz(e.target.value) })}
            />
          </Field>
          <Field label="Address">
            <input
              className={inputClass}
              value={str(draft.address)}
              onChange={(e) => onChange({ address: nz(e.target.value) })}
            />
          </Field>
          <Field label="Website">
            <input
              className={inputClass}
              inputMode="url"
              autoCapitalize="none"
              value={draft.website}
              onChange={(e) => onChange({ website: e.target.value })}
            />
          </Field>
        </Card>

        {/* 2 — Services */}
        <Card icon={<WrenchIcon />} title="Services" badge={badge}>
          <CategoryGrid
            selected={draft.serviceCategories}
            onToggle={(value) =>
              onChange({
                serviceCategories: draft.serviceCategories.includes(value)
                  ? draft.serviceCategories.filter((c) => c !== value)
                  : [...draft.serviceCategories, value],
              })
            }
          />
          <TagEditor
            label="Specific services you offer"
            values={draft.servicesOffered}
            onChange={(servicesOffered) => onChange({ servicesOffered })}
            placeholder="e.g. Water heater repair"
          />
          <CapabilityPicker
            categories={draft.serviceCategories}
            selected={draft.capabilities}
            onChange={(capabilities) => onChange({ capabilities })}
          />
          <Toggle
            label="Emergency / 24-7 service"
            hint="We'll flag you for urgent jobs."
            checked={draft.emergencyService === true}
            onChange={(v) => onChange({ emergencyService: v })}
          />
        </Card>

        {/* 3 — Service Area */}
        <Card icon={<MapPinIcon />} title="Service area" badge={badge}>
          <Field label="Where you work">
            <input
              className={inputClass}
              value={str(draft.serviceAreaText)}
              onChange={(e) => onChange({ serviceAreaText: nz(e.target.value) })}
              placeholder="e.g. The Quad Cities and surrounding towns"
            />
          </Field>
          <TagEditor
            label="Cities / towns"
            values={draft.serviceAreaCities}
            onChange={(serviceAreaCities) => onChange({ serviceAreaCities })}
            placeholder="Add a city"
          />
          <p className="text-xs text-charcoal">
            You&rsquo;ll pin your exact coverage on a map in a moment — this is just the
            description.
          </p>
        </Card>

        {/* 4 — About Us */}
        <Card icon={<DocIcon />} title="About us" badge={badge}>
          <Field label="About your business">
            <textarea
              className={cn(inputClass, "min-h-[96px] py-3")}
              rows={4}
              maxLength={2000}
              value={str(draft.description)}
              onChange={(e) => onChange({ description: nz(e.target.value) })}
            />
          </Field>
          <Field label="Years in business">
            <input
              className={inputClass}
              inputMode="numeric"
              value={draft.yearsInBusiness ?? ""}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "");
                onChange({ yearsInBusiness: digits ? Number(digits) : null });
              }}
            />
          </Field>
        </Card>

        {/* 5 — Photos */}
        <Card icon={<CameraIcon />} title="Photos" badge={draft.photos.length ? badge : undefined}>
          <PhotoGrid
            photos={draft.photos}
            onRemove={(url) => onChange({ photos: draft.photos.filter((p) => p !== url) })}
          />
        </Card>

        {/* 6 — Trust & Credentials */}
        <Card icon={<ShieldIcon />} title="Trust &amp; credentials" badge={badge}>
          <TagEditor
            label="Certifications"
            values={draft.certifications}
            onChange={(certifications) => onChange({ certifications })}
            placeholder="e.g. EPA 608 Certified"
          />
          <TagEditor
            label="Licenses mentioned"
            values={draft.licenses}
            onChange={(licenses) => onChange({ licenses })}
            placeholder="Add a license"
          />
          <TagEditor
            label="Awards"
            values={draft.awards}
            onChange={(awards) => onChange({ awards })}
            placeholder="Add an award"
          />
          <Toggle
            label="Insurance mentioned on site"
            checked={draft.insuranceMentioned === true}
            onChange={(v) => onChange({ insuranceMentioned: v })}
          />
          {draft.testimonials.length > 0 && (
            <div>
              <span className="mb-1 block text-sm font-medium text-charcoal">Review snippets</span>
              <div className="space-y-3">
                {draft.testimonials.map((t, i) => (
                  <div key={i} className="rounded-xl border border-charcoal/15 p-3">
                    <textarea
                      className={cn(inputClass, "min-h-[64px] py-2 text-sm")}
                      rows={2}
                      value={t.quote}
                      onChange={(e) => {
                        const next = [...draft.testimonials];
                        next[i] = { ...t, quote: e.target.value };
                        onChange({ testimonials: next });
                      }}
                    />
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        className={cn(inputClass, "h-9 text-sm")}
                        placeholder="Author (optional)"
                        value={t.author ?? ""}
                        onChange={(e) => {
                          const next = [...draft.testimonials];
                          next[i] = { ...t, author: e.target.value || null };
                          onChange({ testimonials: next });
                        }}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          onChange({ testimonials: draft.testimonials.filter((_, j) => j !== i) })
                        }
                        className="shrink-0 text-sm font-medium text-charcoal"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      <div className="mt-8">
        <PrimaryButton onClick={onContinue}>Looks good — create my account</PrimaryButton>
        <p className="mt-3 text-center text-xs text-charcoal">
          Next: a quick account so you can get job leads.
        </p>
      </div>
    </main>
  );
}

function CategoryGrid({
  selected,
  onToggle,
}: {
  selected: string[];
  onToggle: (value: string) => void;
}) {
  return (
    <fieldset>
      <legend className="mb-2 block text-sm font-medium text-charcoal">Service categories</legend>
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(categoryLabels).map(([value, label]) => {
          const checked = selected.includes(value);
          return (
            <label
              key={value}
              className={cn(
                "flex min-h-[44px] cursor-pointer items-center gap-2 rounded-xl border px-3 text-sm font-medium",
                checked
                  ? "border-brand-700 bg-brand-50 text-brand-800"
                  : "border-charcoal/30 bg-white text-charcoal",
              )}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(value)}
                className="h-4 w-4 accent-brand-700"
              />
              {label}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

/**
 * Specialty multi-select, scoped to the currently-selected categories. Hidden
 * when those categories have no sub-vocab (e.g. general handyman). Leaving it
 * empty just means "generalist" — eligible for every job in your categories.
 */
function CapabilityPicker({
  categories,
  selected,
  onChange,
}: {
  categories: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const options = capabilityOptionsFor(categories);
  if (options.length === 0) return null;
  return (
    <div>
      <span className="mb-1 block text-sm font-medium text-charcoal">
        What do you specialize in?
      </span>
      <p className="mb-2 text-xs text-charcoal">
        Helps us send you the right jobs. Leave blank if you do it all.
      </p>
      <div className="flex flex-wrap gap-2">
        {options.map(({ value, label }) => {
          const checked = selected.includes(value);
          return (
            <button
              key={value}
              type="button"
              onClick={() =>
                onChange(checked ? selected.filter((c) => c !== value) : [...selected, value])
              }
              className={cn(
                "min-h-[40px] rounded-full border px-4 text-sm font-medium",
                checked
                  ? "border-brand-700 bg-brand-50 text-brand-800"
                  : "border-charcoal/30 bg-white text-charcoal",
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Null-coalescing helpers: draft scalar fields are `string | null`.
function str(v: string | null): string {
  return v ?? "";
}
function nz(v: string): string | null {
  return v.trim() ? v : null;
}

// --- inline stroke icons ---------------------------------------------------
const iconProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  className: "h-5 w-5",
  "aria-hidden": true,
};
function BuildingIcon() {
  return (
    <svg {...iconProps}>
      <path d="M3 21h18M5 21V5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v16M9 7h.01M12 7h.01M9 11h.01M12 11h.01M9 15h.01M12 15h.01" />
    </svg>
  );
}
function WrenchIcon() {
  return (
    <svg {...iconProps}>
      <path d="M14.7 6.3a4 4 0 0 1-5 5L4 17v3h3l5.7-5.7a4 4 0 0 1 5-5l-2.3 2.3-1.7-1.7 2.3-2.3a4 4 0 0 0-1.3-.6z" />
    </svg>
  );
}
function MapPinIcon() {
  return (
    <svg {...iconProps}>
      <path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}
function DocIcon() {
  return (
    <svg {...iconProps}>
      <path d="M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5zM8 13h8M8 17h6" />
    </svg>
  );
}
function CameraIcon() {
  return (
    <svg {...iconProps}>
      <path d="M3 8a2 2 0 0 1 2-2h2l1.5-2h7L17 6h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <circle cx="12" cy="12.5" r="3.5" />
    </svg>
  );
}
function ShieldIcon() {
  return (
    <svg {...iconProps}>
      <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3zM9.5 12l2 2 3.5-3.5" />
    </svg>
  );
}
